import { useEffect } from 'react';
import { useSealFlow } from '../lib/seal/useSealFlow';
import { formatLongDate } from '../lib/format';
import type { MessageDraft } from './ScheduleView';
import './record.css';

/**
 * The confirmation bottom sheet (prototype board 06). Confirming runs the real
 * seal (encrypt → upload → finalize → seal → trigger) via useSealFlow; on
 * success it calls onSealed (the caller navigates to the vault).
 */
export function SealSheet({ draft, onCancel, onSealed }: {
  draft: MessageDraft;
  onCancel: () => void;
  onSealed: () => void;
}) {
  const { status, error, seal } = useSealFlow();
  useEffect(() => { if (status === 'sealed') onSealed(); }, [status, onSealed]);
  const busy = status === 'sealing';

  return (
    <div className="sealsheet__scrim" role="dialog" aria-modal="true" aria-label="Seal this message">
      <div className="sealsheet">
        <div className="sealsheet__grip" aria-hidden="true" />
        <p className="sealsheet__kicker">Seal</p>
        <h2 className="sealsheet__title">Seal this message?</h2>
        <p className="sealsheet__summary">
          For {draft.recipientName},{' '}
          {(draft.conditions?.length ?? 0) > 1
            ? 'to arrive when the conditions you set are met'
            : draft.deliveryDate === ''
              ? 'to arrive at the moment you chose'
              : `to arrive on ${formatLongDate(draft.deliveryDate)}`}
          . Once sealed, it is encrypted so only they can open it — at its moment.
        </p>
        {busy && <p className="sealsheet__status" role="status">Sealing your message…</p>}
        {status === 'error' && <p className="sealsheet__alert" role="alert">{error}</p>}
        <div className="sealsheet__actions">
          <div className="la-cta-wrap">
            <button className="la-btn-primary" disabled={busy} onClick={() => void seal(draft)}>
              {status === 'error' ? 'Try again' : 'Seal this message'}
            </button>
          </div>
          <button className="la-btn-secondary" disabled={busy} onClick={onCancel}>Not yet</button>
        </div>
      </div>
    </div>
  );
}
